import React, { useCallback, useEffect, useRef, useState } from 'react'
import { httpsCallable } from 'firebase/functions'
import { getFns } from '../../../firebase'
import { readCurrentStoreLocation, storeLocationErrorMessage } from '../../../lib/storeLocation'
import Button from '../../../components/ui/Button'
import Input from '../../../components/ui/Input'
import Card, { CardHeader, CardTitle, CardDescription, CardContent } from '../../../components/ui/Card'

const METERS_PER_MILE = 1609.344
const toDraft = (data) => ({
  label: data.label || '',
  latitude: data.center ? String(data.center.latitude) : '',
  longitude: data.center ? String(data.center.longitude) : '',
  radius: String(data.radiusMeters / METERS_PER_MILE),
  radiusUnit: 'miles',
  maxAccuracyMeters: String(data.maxAccuracyMeters),
})
const validSettings = (data) => Number.isInteger(data?.revision) && Number.isFinite(data?.radiusMeters) && Number.isInteger(data?.maxAccuracyMeters) && (data.center === null || (Number.isFinite(data.center?.latitude) && Number.isFinite(data.center?.longitude)))

export default function StoreLocationSettings() {
  const [saved, setSaved] = useState(null)
  const [draft, setDraft] = useState({ label: '', latitude: '', longitude: '', radius: '2', radiusUnit: 'miles', maxAccuracyMeters: '50' })
  const [busy, setBusy] = useState('load')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [conflict, setConflict] = useState(false)
  const mountedRef = useRef(false)
  const requestRef = useRef(0)

  const load = useCallback(async ({ preserveDraft = false } = {}) => {
    const request = ++requestRef.current
    setBusy('load')
    setError('')
    setNotice('')
    try {
      const { data } = await httpsCallable(getFns(), 'storeLocationAdminGet')({})
      if (!mountedRef.current || request !== requestRef.current) return
      if (!validSettings(data)) throw new Error('Invalid location settings response')
      setSaved(data)
      if (!preserveDraft) setDraft(toDraft(data))
      else setNotice('Latest saved location loaded below. Your edits are unchanged. Review them before saving.')
      setConflict(false)
    } catch {
      if (!mountedRef.current || request !== requestRef.current) return
      setError('Could not load store location settings. Please try again.')
    } finally {
      if (mountedRef.current && request === requestRef.current) setBusy('')
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    load()
    return () => { mountedRef.current = false; ++requestRef.current }
  }, [load])

  const edit = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }))
    setNotice('')
  }

  const changeRadiusUnit = (unit) => {
    setDraft((current) => {
      const meters = Number(current.radius) * (current.radiusUnit === 'miles' ? METERS_PER_MILE : 1)
      return { ...current, radiusUnit: unit, radius: current.radius === '' ? '' : String(unit === 'miles' ? meters / METERS_PER_MILE : meters) }
    })
    setNotice('')
  }

  const useCurrentLocation = async () => {
    const request = ++requestRef.current
    setBusy('location')
    setError('')
    setNotice('')
    try {
      const location = await readCurrentStoreLocation()
      if (!mountedRef.current || request !== requestRef.current) return
      setDraft((current) => ({ ...current, latitude: String(location.latitude), longitude: String(location.longitude) }))
      setNotice(`Current location added to the form (reported accuracy ±${Math.round(location.accuracy)} m). Confirm you are at the store and review the coordinates, then save to apply them.`)
    } catch (err) {
      if (mountedRef.current && request === requestRef.current) setError(storeLocationErrorMessage(err))
    } finally {
      if (mountedRef.current && request === requestRef.current) setBusy('')
    }
  }

  const save = async (event) => {
    event.preventDefault()
    if (!saved || busy || conflict) return
    setError('')
    setNotice('')
    const latitudeText = draft.latitude.trim()
    const longitudeText = draft.longitude.trim()
    const center = latitudeText === '' && longitudeText === '' ? null : { latitude: Number(latitudeText), longitude: Number(longitudeText) }
    const label = draft.label.trim()
    const radiusMeters = Number(draft.radius) * (draft.radiusUnit === 'miles' ? METERS_PER_MILE : 1)
    const maxAccuracyMeters = Number(draft.maxAccuracyMeters)
    if (center && (!latitudeText || !longitudeText || !Number.isFinite(center.latitude) || !Number.isFinite(center.longitude) || Math.abs(center.latitude) > 90 || Math.abs(center.longitude) > 180)) {
      setError('Enter both coordinates: latitude from −90 to 90 and longitude from −180 to 180, or leave both empty.')
      return
    }
    if (label.length > 160) {
      setError('Use no more than 160 characters for the store address.')
      return
    }
    if (!Number.isFinite(radiusMeters) || radiusMeters < 25 || radiusMeters > 10000) {
      setError('Enter a radius from 25 to 10,000 meters (about 0.016 to 6.214 miles).')
      return
    }
    if (!Number.isInteger(maxAccuracyMeters) || maxAccuracyMeters < 5 || maxAccuracyMeters > 100 || maxAccuracyMeters > radiusMeters) {
      setError('Enter a whole-number maximum location error from 5 to 100 meters, no greater than the radius.')
      return
    }
    const request = ++requestRef.current
    setBusy('save')
    try {
      const { data } = await httpsCallable(getFns(), 'storeLocationAdminSave')({ label, center, radiusMeters, maxAccuracyMeters, revision: saved.revision })
      if (!mountedRef.current || request !== requestRef.current) return
      if (!validSettings(data)) throw new Error('Invalid location settings response')
      setSaved(data)
      setDraft(toDraft(data))
      setNotice(data.center ? 'Store location settings saved.' : 'Settings saved. No presence check will be approved until a store location is added.')
    } catch (err) {
      if (!mountedRef.current || request !== requestRef.current) return
      if (err?.code === 'functions/aborted') {
        setConflict(true)
        setError('Another administrator changed these settings. Your edits are still here. Review the latest saved location before saving.')
      } else if (err?.code === 'functions/invalid-argument') {
        setError(err.message || 'Check the location settings and try again.')
      } else {
        setConflict(true)
        setError('Could not confirm the save. Your edits are still here. Review the latest settings before trying again.')
      }
    } finally {
      if (mountedRef.current && request === requestRef.current) setBusy('')
    }
  }

  const disabled = !saved || Boolean(busy)
  const draftRadiusMeters = Number(draft.radius) * (draft.radiusUnit === 'miles' ? METERS_PER_MILE : 1)
  const radiusConversion = draft.radius !== '' && Number.isFinite(draftRadiusMeters)
    ? `${(draftRadiusMeters / METERS_PER_MILE).toLocaleString('en-US', { maximumFractionDigits: 6 })} miles = ${draftRadiusMeters.toLocaleString('en-US', { maximumFractionDigits: 3 })} meters.`
    : 'Choose miles or meters. Allowed range: 25–10,000 meters.'

  return <Card variant="solid" hover="none">
    <CardHeader>
      <CardTitle>Store Location</CardTitle>
      <CardDescription>Set the store center and allowed area for location checks.</CardDescription>
    </CardHeader>
    <CardContent className="mt-6 space-y-6">
      {busy === 'load' && <p role="status" className="text-sm text-muted-foreground">Loading store location…</p>}
      <form onSubmit={save} className="space-y-5">
        <Input id="store-location-label" label="Store address" maxLength={160} value={draft.label} onChange={(event) => edit('label', event.target.value)} disabled={disabled} description="A label for this location. The coordinates below determine the store center." />
        <div>
          <p className="text-sm leading-relaxed text-muted-foreground">Stand at the store before using your current location. This fills the coordinates only; review and save to apply them.</p>
          <Button type="button" variant="outline" size="sm" iconName="MapPin" className="mt-3" onClick={useCurrentLocation} disabled={disabled} loading={busy === 'location'}>Use current location</Button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input id="store-location-latitude" label="Latitude" type="number" step="any" min={-90} max={90} inputMode="decimal" value={draft.latitude} onChange={(event) => edit('latitude', event.target.value)} disabled={disabled} />
          <Input id="store-location-longitude" label="Longitude" type="number" step="any" min={-180} max={180} inputMode="decimal" value={draft.longitude} onChange={(event) => edit('longitude', event.target.value)} disabled={disabled} />
          <div className="space-y-2">
            <Input id="store-location-radius" label={`Allowed radius (${draft.radiusUnit})`} type="number" step="any" min={25 / (draft.radiusUnit === 'miles' ? METERS_PER_MILE : 1)} max={10000 / (draft.radiusUnit === 'miles' ? METERS_PER_MILE : 1)} inputMode="decimal" value={draft.radius} onChange={(event) => edit('radius', event.target.value)} disabled={disabled} />
            <label htmlFor="store-location-radius-unit" className="text-xs text-muted-foreground">Radius unit</label>
            <select id="store-location-radius-unit" value={draft.radiusUnit} onChange={(event) => changeRadiusUnit(event.target.value)} disabled={disabled} className="block h-11 w-full rounded-lg border border-border bg-card px-3 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-40 md:h-10 md:text-sm">
              <option value="miles">Miles</option>
              <option value="meters">Meters</option>
            </select>
            <p className="text-xs leading-relaxed text-muted-foreground">{radiusConversion}</p>
          </div>
          <Input id="store-location-accuracy" label="Maximum location error (meters)" type="number" step={1} min={5} max={100} inputMode="numeric" value={draft.maxAccuracyMeters} onChange={(event) => edit('maxAccuracyMeters', event.target.value)} disabled={disabled} description="5–100 m, no greater than the radius. Less accurate readings will require another check." />
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">Leave both coordinates empty to remove the store location. No presence check will be approved until a location is saved.</p>

        {error && <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
        {notice && <p role="status" className="text-sm text-foreground">{notice}</p>}
        {conflict && <Button type="button" variant="outline" disabled={Boolean(busy)} onClick={() => load({ preserveDraft: true })}>Review latest settings</Button>}
        {!saved && !busy && <Button type="button" variant="outline" onClick={() => load()}>Retry</Button>}

        {saved && <div className="rounded-lg bg-muted/40 p-3">
          <p className="text-xs font-medium text-foreground">Saved location</p>
          {saved.label && <p className="mt-1 text-xs text-muted-foreground">{saved.label}</p>}
          {saved.center ? <div className="mt-2 space-y-1 text-xs text-muted-foreground">
            <p className="break-all">{saved.center.latitude}, {saved.center.longitude}</p>
            <p>Radius: {(saved.radiusMeters / METERS_PER_MILE).toLocaleString('en-US', { maximumFractionDigits: 6 })} miles ({saved.radiusMeters.toLocaleString('en-US', { maximumFractionDigits: 3 })} m)</p>
            <p>Maximum location error: {saved.maxAccuracyMeters} m</p>
          </div> : <p className="mt-1 text-xs text-muted-foreground">No store location saved.</p>}
        </div>}
        <Button type="submit" iconName="Save" loading={busy === 'save'} disabled={disabled || conflict}>Save store location</Button>
      </form>
      <p className="border-t border-border pt-4 text-xs leading-relaxed text-muted-foreground">The location reading and its accuracy must fit within the allowed area. Store presence also requires a match with the saved store network. Location is checked only when requested.</p>
    </CardContent>
  </Card>
}
